/**
 * ISO-3166 alpha-2 codes and E.164 calling codes.
 *
 * **This is reference data, not clinic data** — every country's code is fixed
 * by international standard, the same for every clinic that will ever run
 * DentaCore, unlike a branch name or a procedure price. CLAUDE.md's *Data is
 * configured, not coded* is about facts an admin enters for their own clinic;
 * this list is closer to the FDI tooth numbering already hardcoded in
 * `tooth-geometry.ts` — a fixed standard, not a business decision.
 *
 * Sorted by country name. Several countries share a calling code (the NANP
 * bloc all carry `+1`) — that is correct, not a bug.
 */
export interface Country {
  /** ISO-3166 alpha-2, matches `clinics.default_country`. */
  iso2: string;
  name: string;
  /** E.164 calling code, `+` included. */
  dialCode: string;
}

export const COUNTRIES: readonly Country[] = [
  { iso2: "AF", name: "Afghanistan", dialCode: "+93" },
  { iso2: "AL", name: "Albania", dialCode: "+355" },
  { iso2: "DZ", name: "Algeria", dialCode: "+213" },
  { iso2: "AD", name: "Andorra", dialCode: "+376" },
  { iso2: "AO", name: "Angola", dialCode: "+244" },
  { iso2: "AR", name: "Argentina", dialCode: "+54" },
  { iso2: "AM", name: "Armenia", dialCode: "+374" },
  { iso2: "AU", name: "Australia", dialCode: "+61" },
  { iso2: "AT", name: "Austria", dialCode: "+43" },
  { iso2: "AZ", name: "Azerbaijan", dialCode: "+994" },
  { iso2: "BH", name: "Bahrain", dialCode: "+973" },
  { iso2: "BD", name: "Bangladesh", dialCode: "+880" },
  { iso2: "BY", name: "Belarus", dialCode: "+375" },
  { iso2: "BE", name: "Belgium", dialCode: "+32" },
  { iso2: "BZ", name: "Belize", dialCode: "+501" },
  { iso2: "BJ", name: "Benin", dialCode: "+229" },
  { iso2: "BT", name: "Bhutan", dialCode: "+975" },
  { iso2: "BO", name: "Bolivia", dialCode: "+591" },
  { iso2: "BA", name: "Bosnia and Herzegovina", dialCode: "+387" },
  { iso2: "BW", name: "Botswana", dialCode: "+267" },
  { iso2: "BR", name: "Brazil", dialCode: "+55" },
  { iso2: "BN", name: "Brunei", dialCode: "+673" },
  { iso2: "BG", name: "Bulgaria", dialCode: "+359" },
  { iso2: "BF", name: "Burkina Faso", dialCode: "+226" },
  { iso2: "BI", name: "Burundi", dialCode: "+257" },
  { iso2: "KH", name: "Cambodia", dialCode: "+855" },
  { iso2: "CM", name: "Cameroon", dialCode: "+237" },
  { iso2: "CA", name: "Canada", dialCode: "+1" },
  { iso2: "CV", name: "Cape Verde", dialCode: "+238" },
  { iso2: "CF", name: "Central African Republic", dialCode: "+236" },
  { iso2: "TD", name: "Chad", dialCode: "+235" },
  { iso2: "CL", name: "Chile", dialCode: "+56" },
  { iso2: "CN", name: "China", dialCode: "+86" },
  { iso2: "CO", name: "Colombia", dialCode: "+57" },
  { iso2: "KM", name: "Comoros", dialCode: "+269" },
  { iso2: "CG", name: "Congo", dialCode: "+242" },
  { iso2: "CD", name: "Congo (DRC)", dialCode: "+243" },
  { iso2: "CR", name: "Costa Rica", dialCode: "+506" },
  { iso2: "HR", name: "Croatia", dialCode: "+385" },
  { iso2: "CU", name: "Cuba", dialCode: "+53" },
  { iso2: "CY", name: "Cyprus", dialCode: "+357" },
  { iso2: "CZ", name: "Czechia", dialCode: "+420" },
  { iso2: "DK", name: "Denmark", dialCode: "+45" },
  { iso2: "DJ", name: "Djibouti", dialCode: "+253" },
  { iso2: "DO", name: "Dominican Republic", dialCode: "+1" },
  { iso2: "EC", name: "Ecuador", dialCode: "+593" },
  { iso2: "EG", name: "Egypt", dialCode: "+20" },
  { iso2: "SV", name: "El Salvador", dialCode: "+503" },
  { iso2: "GQ", name: "Equatorial Guinea", dialCode: "+240" },
  { iso2: "ER", name: "Eritrea", dialCode: "+291" },
  { iso2: "EE", name: "Estonia", dialCode: "+372" },
  { iso2: "SZ", name: "Eswatini", dialCode: "+268" },
  { iso2: "ET", name: "Ethiopia", dialCode: "+251" },
  { iso2: "FJ", name: "Fiji", dialCode: "+679" },
  { iso2: "FI", name: "Finland", dialCode: "+358" },
  { iso2: "FR", name: "France", dialCode: "+33" },
  { iso2: "GA", name: "Gabon", dialCode: "+241" },
  { iso2: "GM", name: "Gambia", dialCode: "+220" },
  { iso2: "GE", name: "Georgia", dialCode: "+995" },
  { iso2: "DE", name: "Germany", dialCode: "+49" },
  { iso2: "GH", name: "Ghana", dialCode: "+233" },
  { iso2: "GR", name: "Greece", dialCode: "+30" },
  { iso2: "GT", name: "Guatemala", dialCode: "+502" },
  { iso2: "GN", name: "Guinea", dialCode: "+224" },
  { iso2: "GW", name: "Guinea-Bissau", dialCode: "+245" },
  { iso2: "GY", name: "Guyana", dialCode: "+592" },
  { iso2: "HT", name: "Haiti", dialCode: "+509" },
  { iso2: "HN", name: "Honduras", dialCode: "+504" },
  { iso2: "HK", name: "Hong Kong", dialCode: "+852" },
  { iso2: "HU", name: "Hungary", dialCode: "+36" },
  { iso2: "IS", name: "Iceland", dialCode: "+354" },
  { iso2: "IN", name: "India", dialCode: "+91" },
  { iso2: "ID", name: "Indonesia", dialCode: "+62" },
  { iso2: "IR", name: "Iran", dialCode: "+98" },
  { iso2: "IQ", name: "Iraq", dialCode: "+964" },
  { iso2: "IE", name: "Ireland", dialCode: "+353" },
  { iso2: "IL", name: "Israel", dialCode: "+972" },
  { iso2: "IT", name: "Italy", dialCode: "+39" },
  { iso2: "CI", name: "Ivory Coast", dialCode: "+225" },
  { iso2: "JM", name: "Jamaica", dialCode: "+1" },
  { iso2: "JP", name: "Japan", dialCode: "+81" },
  { iso2: "JO", name: "Jordan", dialCode: "+962" },
  { iso2: "KZ", name: "Kazakhstan", dialCode: "+7" },
  { iso2: "KE", name: "Kenya", dialCode: "+254" },
  { iso2: "KW", name: "Kuwait", dialCode: "+965" },
  { iso2: "KG", name: "Kyrgyzstan", dialCode: "+996" },
  { iso2: "LA", name: "Laos", dialCode: "+856" },
  { iso2: "LV", name: "Latvia", dialCode: "+371" },
  { iso2: "LB", name: "Lebanon", dialCode: "+961" },
  { iso2: "LS", name: "Lesotho", dialCode: "+266" },
  { iso2: "LR", name: "Liberia", dialCode: "+231" },
  { iso2: "LY", name: "Libya", dialCode: "+218" },
  { iso2: "LI", name: "Liechtenstein", dialCode: "+423" },
  { iso2: "LT", name: "Lithuania", dialCode: "+370" },
  { iso2: "LU", name: "Luxembourg", dialCode: "+352" },
  { iso2: "MO", name: "Macau", dialCode: "+853" },
  { iso2: "MG", name: "Madagascar", dialCode: "+261" },
  { iso2: "MW", name: "Malawi", dialCode: "+265" },
  { iso2: "MY", name: "Malaysia", dialCode: "+60" },
  { iso2: "MV", name: "Maldives", dialCode: "+960" },
  { iso2: "ML", name: "Mali", dialCode: "+223" },
  { iso2: "MT", name: "Malta", dialCode: "+356" },
  { iso2: "MR", name: "Mauritania", dialCode: "+222" },
  { iso2: "MU", name: "Mauritius", dialCode: "+230" },
  { iso2: "MX", name: "Mexico", dialCode: "+52" },
  { iso2: "MD", name: "Moldova", dialCode: "+373" },
  { iso2: "MC", name: "Monaco", dialCode: "+377" },
  { iso2: "MN", name: "Mongolia", dialCode: "+976" },
  { iso2: "ME", name: "Montenegro", dialCode: "+382" },
  { iso2: "MA", name: "Morocco", dialCode: "+212" },
  { iso2: "MZ", name: "Mozambique", dialCode: "+258" },
  { iso2: "MM", name: "Myanmar", dialCode: "+95" },
  { iso2: "NA", name: "Namibia", dialCode: "+264" },
  { iso2: "NP", name: "Nepal", dialCode: "+977" },
  { iso2: "NL", name: "Netherlands", dialCode: "+31" },
  { iso2: "NZ", name: "New Zealand", dialCode: "+64" },
  { iso2: "NI", name: "Nicaragua", dialCode: "+505" },
  { iso2: "NE", name: "Niger", dialCode: "+227" },
  { iso2: "NG", name: "Nigeria", dialCode: "+234" },
  { iso2: "MK", name: "North Macedonia", dialCode: "+389" },
  { iso2: "NO", name: "Norway", dialCode: "+47" },
  { iso2: "OM", name: "Oman", dialCode: "+968" },
  { iso2: "PK", name: "Pakistan", dialCode: "+92" },
  { iso2: "PS", name: "Palestine", dialCode: "+970" },
  { iso2: "PA", name: "Panama", dialCode: "+507" },
  { iso2: "PG", name: "Papua New Guinea", dialCode: "+675" },
  { iso2: "PY", name: "Paraguay", dialCode: "+595" },
  { iso2: "PE", name: "Peru", dialCode: "+51" },
  { iso2: "PH", name: "Philippines", dialCode: "+63" },
  { iso2: "PL", name: "Poland", dialCode: "+48" },
  { iso2: "PT", name: "Portugal", dialCode: "+351" },
  { iso2: "QA", name: "Qatar", dialCode: "+974" },
  { iso2: "RO", name: "Romania", dialCode: "+40" },
  { iso2: "RU", name: "Russia", dialCode: "+7" },
  { iso2: "RW", name: "Rwanda", dialCode: "+250" },
  { iso2: "SA", name: "Saudi Arabia", dialCode: "+966" },
  { iso2: "SN", name: "Senegal", dialCode: "+221" },
  { iso2: "RS", name: "Serbia", dialCode: "+381" },
  { iso2: "SC", name: "Seychelles", dialCode: "+248" },
  { iso2: "SL", name: "Sierra Leone", dialCode: "+232" },
  { iso2: "SG", name: "Singapore", dialCode: "+65" },
  { iso2: "SK", name: "Slovakia", dialCode: "+421" },
  { iso2: "SI", name: "Slovenia", dialCode: "+386" },
  { iso2: "SO", name: "Somalia", dialCode: "+252" },
  { iso2: "ZA", name: "South Africa", dialCode: "+27" },
  { iso2: "KR", name: "South Korea", dialCode: "+82" },
  { iso2: "SS", name: "South Sudan", dialCode: "+211" },
  { iso2: "ES", name: "Spain", dialCode: "+34" },
  { iso2: "LK", name: "Sri Lanka", dialCode: "+94" },
  { iso2: "SD", name: "Sudan", dialCode: "+249" },
  { iso2: "SR", name: "Suriname", dialCode: "+597" },
  { iso2: "SE", name: "Sweden", dialCode: "+46" },
  { iso2: "CH", name: "Switzerland", dialCode: "+41" },
  { iso2: "SY", name: "Syria", dialCode: "+963" },
  { iso2: "TW", name: "Taiwan", dialCode: "+886" },
  { iso2: "TJ", name: "Tajikistan", dialCode: "+992" },
  { iso2: "TZ", name: "Tanzania", dialCode: "+255" },
  { iso2: "TH", name: "Thailand", dialCode: "+66" },
  { iso2: "TG", name: "Togo", dialCode: "+228" },
  { iso2: "TT", name: "Trinidad and Tobago", dialCode: "+1" },
  { iso2: "TN", name: "Tunisia", dialCode: "+216" },
  { iso2: "TR", name: "Turkey", dialCode: "+90" },
  { iso2: "TM", name: "Turkmenistan", dialCode: "+993" },
  { iso2: "UG", name: "Uganda", dialCode: "+256" },
  { iso2: "UA", name: "Ukraine", dialCode: "+380" },
  { iso2: "AE", name: "United Arab Emirates", dialCode: "+971" },
  { iso2: "GB", name: "United Kingdom", dialCode: "+44" },
  { iso2: "US", name: "United States", dialCode: "+1" },
  { iso2: "UY", name: "Uruguay", dialCode: "+598" },
  { iso2: "UZ", name: "Uzbekistan", dialCode: "+998" },
  { iso2: "VE", name: "Venezuela", dialCode: "+58" },
  { iso2: "VN", name: "Vietnam", dialCode: "+84" },
  { iso2: "YE", name: "Yemen", dialCode: "+967" },
  { iso2: "ZM", name: "Zambia", dialCode: "+260" },
  { iso2: "ZW", name: "Zimbabwe", dialCode: "+263" },
];

const BY_ISO2 = new Map(COUNTRIES.map((country) => [country.iso2, country]));

/** Dial codes as they appear in the list, longest first — matching a number against them takes the longest (most specific) match. */
const DIAL_CODES_LONGEST_FIRST = [...new Set(COUNTRIES.map((country) => country.dialCode))].sort(
  (a, b) => b.length - a.length,
);

export function countryByIso2(iso2: string): Country | undefined {
  return BY_ISO2.get(iso2.toUpperCase());
}

/**
 * Regional-indicator flag emoji from an ISO-3166 alpha-2 code — no image
 * assets, no CDN fetch, just two Unicode code points offset from "A".
 */
export function flagEmoji(iso2: string): string {
  const codePoints = [...iso2.toUpperCase()].map((char) => 0x1f1e6 + char.charCodeAt(0) - 65);
  return String.fromCodePoint(...codePoints);
}

/**
 * Splits an E.164 number into its country and the digits after the dial
 * code, for prefilling the picker when editing a patient who already has a
 * phone number on file. Falls back to no country / the whole string as the
 * national number when nothing matches — never guessed, just left blank.
 */
export function splitE164(value: string): { iso2: string | null; national: string } {
  if (!value.startsWith("+")) return { iso2: null, national: value };

  for (const dialCode of DIAL_CODES_LONGEST_FIRST) {
    if (value.startsWith(dialCode)) {
      const match = COUNTRIES.find((country) => country.dialCode === dialCode);
      return { iso2: match?.iso2 ?? null, national: value.slice(dialCode.length).trim() };
    }
  }

  return { iso2: null, national: value.slice(1) };
}

/**
 * `+96181869935` → `+961 81869935`, for reading rather than for storage.
 *
 * **The dial code, a space, then the rest** (client, 2026-09-07: *"let's have
 * it as +961 (or whatever country code) followed by the phone number so +961
 * 81869935"*). Stored numbers are E.164 and printed as one 12-digit run, which
 * is correct as a value and unreadable as a fact about a person — nothing in
 * `+96181869935` tells you where the country ends and the subscriber begins.
 *
 * Deliberately **not** libphonenumber's `formatInternational()`, which would
 * give `+961 81 869 935`. That splits the national number into carrier and
 * subscriber blocks, which is right for a phone book and wrong here: the client
 * asked for the number as they say it, and a Lebanese mobile is said as one
 * eight-digit run. This also keeps the formatter free of the metadata bundle —
 * it is a string operation over the dial codes this file already has.
 *
 * Anything that is not a recognisable E.164 number comes back untouched, so a
 * legacy or hand-entered value is shown as it was stored rather than mangled.
 */
export function formatPhone(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  if (!value.startsWith("+")) return value;

  const { iso2, national } = splitE164(value);
  if (iso2 === null || national === "") return value;

  const dialCode = countryByIso2(iso2)?.dialCode;
  return dialCode === undefined ? value : `${dialCode} ${national}`;
}
